import { useState } from "react"
import { Search, Plus, Phone, User } from "lucide-react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { useVendors, useCreateVendor } from "@/hooks/useVendors"
import { useAddEventVendor } from "@/hooks/useEventVendors"
import { useToast } from "@/hooks/use-toast"
import { VENDOR_CATEGORIES, VendorCategoryType } from "@/types/vendor"

interface AddVendorToEventModalProps {
  eventId: string
  children: React.ReactNode
}

export function AddVendorToEventModal({ eventId, children }: AddVendorToEventModalProps) {
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState<"select" | "create">("select")
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedVendor, setSelectedVendor] = useState<string | null>(null)
  const [eventVendorData, setEventVendorData] = useState({
    cost: 0,
    payment_terms: "",
    status: "pending",
    notes: ""
  })
  const [newVendorData, setNewVendorData] = useState({
    name: "",
    category: null as VendorCategoryType | null,
    contact_person: "",
    phone: "",
    email: "",
    website: "",
    address: "",
    description: "",
    logo_url: "",
    tags: [] as string[]
  })

  const { data: vendors, isLoading: vendorsLoading } = useVendors()
  const createVendor = useCreateVendor()
  const addEventVendor = useAddEventVendor()
  const { toast } = useToast()

  const filteredVendors = vendors?.filter(vendor =>
    vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.contact_person.toLowerCase().includes(searchTerm.toLowerCase())
  ) || []

  const handleSelectVendor = (vendorId: string) => {
    setSelectedVendor(vendorId)
  }

  const handleAddToEvent = async () => {
    if (!selectedVendor) return

    try {
      await addEventVendor.mutateAsync({
        event_id: eventId,
        vendor_id: selectedVendor,
        ...eventVendorData
      })
      
      setOpen(false)
      resetForm()
      toast({
        title: "Success",
        description: "Vendor added to event successfully",
      })
    } catch (error) {
      console.error('Error adding vendor to event:', error)
    }
  }

  const handleCreateVendor = async () => {
    if (!newVendorData.name || !newVendorData.category) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      })
      return
    }

    try {
      const newVendor = await createVendor.mutateAsync({
        name: newVendorData.name,
        category: newVendorData.category,
        contact_person: newVendorData.contact_person,
        phone: newVendorData.phone,
        email: newVendorData.email,
        website: newVendorData.website,
        address: newVendorData.address,
        description: newVendorData.description,
        logo_url: newVendorData.logo_url,
        tags: newVendorData.tags
      })
      
      // Add the new vendor to the event
      await addEventVendor.mutateAsync({
        event_id: eventId,
        vendor_id: newVendor.id,
        ...eventVendorData
      })

      setOpen(false)
      resetForm()
      toast({
        title: "Success",
        description: "New vendor created and added to event successfully",
      })
    } catch (error) {
      console.error('Error creating vendor:', error)
    }
  }

  const resetForm = () => {
    setStep("select")
    setSearchTerm("")
    setSelectedVendor(null)
    setEventVendorData({
      cost: 0,
      payment_terms: "",
      status: "pending",
      notes: ""
    })
    setNewVendorData({
      name: "",
      category: null,
      contact_person: "",
      phone: "",
      email: "",
      website: "",
      address: "",
      description: "",
      logo_url: "",
      tags: []
    })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {children}
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {step === "select" ? "Add Vendor to Event" : "Create New Vendor"}
          </DialogTitle>
        </DialogHeader>

        {step === "select" ? (
          <div className="space-y-6">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search vendors..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Vendor List */}
            <div className="space-y-2 max-h-60 overflow-y-auto border rounded-md p-4">
              {vendorsLoading ? (
                <div className="text-center py-4">Loading vendors...</div>
              ) : filteredVendors.length === 0 ? (
                <div className="text-center py-4 text-gray-500">
                  No vendors found
                </div>
              ) : (
                filteredVendors.map((vendor) => (
                  <div
                    key={vendor.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedVendor === vendor.id
                        ? "border-purple-600 bg-purple-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    onClick={() => handleSelectVendor(vendor.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <div>
                            <h4 className="font-semibold text-gray-900">{vendor.name}</h4>
                            <p className="text-sm text-gray-600">{vendor.description}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-2">
                          <Badge variant="outline" className="text-xs">
                            {vendor.category}
                          </Badge>
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <User className="h-3 w-3" />
                            {vendor.contact_person}
                          </div>
                          <div className="flex items-center gap-1 text-sm text-gray-600">
                            <Phone className="h-3 w-3" />
                            {vendor.phone}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Event Vendor Details */}
            {selectedVendor && (
              <div className="space-y-4 border-t pt-4">
                <h3 className="font-semibold text-gray-900">Event Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="cost">Event Cost</Label>
                    <Input
                      id="cost"
                      type="number"
                      placeholder="0"
                      value={eventVendorData.cost || ""}
                      onChange={(e) => setEventVendorData(prev => ({
                        ...prev,
                        cost: parseFloat(e.target.value) || 0
                      }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="status">Status</Label>
                    <select
                      id="status"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      value={eventVendorData.status}
                      onChange={(e) => setEventVendorData(prev => ({
                        ...prev,
                        status: e.target.value
                      }))}
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label htmlFor="payment_terms">Payment Terms</Label>
                  <Input
                    id="payment_terms"
                    placeholder="e.g., Net 30, 50% advance"
                    value={eventVendorData.payment_terms}
                    onChange={(e) => setEventVendorData(prev => ({
                      ...prev,
                      payment_terms: e.target.value
                    }))}
                  />
                </div>
                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <textarea
                    id="notes"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    rows={2}
                    placeholder="Additional notes..."
                    value={eventVendorData.notes}
                    onChange={(e) => setEventVendorData(prev => ({
                      ...prev,
                      notes: e.target.value
                    }))}
                  />
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between gap-3">
              <Button
                variant="outline"
                onClick={() => setStep("create")}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add New Vendor
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleAddToEvent}
                  disabled={!selectedVendor || addEventVendor.isPending}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {addEventVendor.isPending ? "Adding..." : "Add to Event"}
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* New Vendor Form */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="vendor_name">Vendor Name *</Label>
                <Input
                  id="vendor_name"
                  placeholder="Enter vendor name"
                  value={newVendorData.name}
                  onChange={(e) => setNewVendorData(prev => ({
                    ...prev,
                    name: e.target.value
                  }))}
                />
              </div>
              <div>
                <Label htmlFor="category">Category *</Label>
                <select
                  id="category"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={newVendorData.category || ""}
                  onChange={(e) => setNewVendorData(prev => ({
                    ...prev,
                    category: e.target.value as VendorCategoryType || null
                  }))}
                >
                  <option value="">Select category</option>
                  {VENDOR_CATEGORIES.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="contact_person">Contact Person</Label>
                <Input
                  id="contact_person"
                  placeholder="Contact person name"
                  value={newVendorData.contact_person}
                  onChange={(e) => setNewVendorData(prev => ({
                    ...prev,
                    contact_person: e.target.value
                  }))}
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  placeholder="Phone number"
                  value={newVendorData.phone}
                  onChange={(e) => setNewVendorData(prev => ({
                    ...prev,
                    phone: e.target.value
                  }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Email address"
                  value={newVendorData.email}
                  onChange={(e) => setNewVendorData(prev => ({
                    ...prev,
                    email: e.target.value
                  }))}
                />
              </div>
              <div>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  placeholder="Website URL"
                  value={newVendorData.website}
                  onChange={(e) => setNewVendorData(prev => ({
                    ...prev,
                    website: e.target.value
                  }))}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                placeholder="Business address"
                value={newVendorData.address}
                onChange={(e) => setNewVendorData(prev => ({
                  ...prev,
                  address: e.target.value
                }))}
              />
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <textarea
                id="description"
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                rows={3}
                placeholder="Brief description of services"
                value={newVendorData.description}
                onChange={(e) => setNewVendorData(prev => ({
                  ...prev,
                  description: e.target.value
                }))}
              />
            </div>

            {/* Event Details for New Vendor */}
            <div className="border-t pt-4 space-y-4">
              <h3 className="font-semibold text-gray-900">Event Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="new_cost">Event Cost</Label>
                  <Input
                    id="new_cost"
                    type="number"
                    placeholder="0"
                    value={eventVendorData.cost || ""}
                    onChange={(e) => setEventVendorData(prev => ({
                      ...prev,
                      cost: parseFloat(e.target.value) || 0
                    }))}
                  />
                </div>
                <div>
                  <Label htmlFor="new_status">Status</Label>
                  <select
                    id="new_status"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    value={eventVendorData.status}
                    onChange={(e) => setEventVendorData(prev => ({
                      ...prev,
                      status: e.target.value
                    }))}
                  >
                    <option value="pending">Pending</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>
              <div>
                <Label htmlFor="new_payment_terms">Payment Terms</Label>
                <Input
                  id="new_payment_terms"
                  placeholder="e.g., Net 30, 50% advance"
                  value={eventVendorData.payment_terms}
                  onChange={(e) => setEventVendorData(prev => ({
                    ...prev,
                    payment_terms: e.target.value
                  }))}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between gap-3">
              <Button
                variant="outline"
                onClick={() => setStep("select")}
              >
                Back to Selection
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateVendor}
                  disabled={createVendor.isPending || addEventVendor.isPending}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {createVendor.isPending ? "Creating..." : "Create & Add to Event"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
